import { Inertia } from '@inertiajs/inertia';
import { userTypesMap } from 'components/constants/userTypes';
import SearchInput from 'components/inputs/SearchInput';
import SecondaryText from 'components/inputs/SecondaryText';
import RoleInput from 'components/inputs/UserTypeInput';
import Table from 'components/table';
import MainTextCell from 'components/table/cells/MainTextCell';
import NameCell from 'components/table/cells/NameCell';
import useCustomTable from 'components/table/useCustomtable';
import { handleQueryFilter } from 'lib/handleQueryFilter';
import { handleQuerySort } from 'lib/handleQuerySort';
import PageLayout from 'pages/PageLayout';
import queryString from 'query-string';
import React, { useEffect, useMemo, useRef } from 'react';

type User = { id: number; username: string };

type Props = {
  data: User[];
  pagy_metadata: any;
};

export default function Index(props: Props) {
  const { data, pagy_metadata } = props;

  const [userTypeFilter, setUserTypeFilter] = React.useState(
    handleQueryFilter('user_types', { number: false }),
  );

  const loadRef = useRef(false);

  const [searchFilter, setSearchFilter] = React.useState('');

  const sortBy = useMemo(() => handleQuerySort(queryString), [queryString]);

  const columns = React.useMemo(
    () => [
      {
        Header: 'Username',
        accessor: 'username',
        Cell: ({ cell }) => <MainTextCell value={cell.value} />,
      },
      {
        Header: 'Name',
        accessor: 'last_name',
        Cell: ({ row }) => <NameCell value={row.original} />,
      },
      {
        Header: 'Type',
        accessor: 'type',
        Cell: ({ cell }) => <SecondaryText value={userTypesMap[cell.value]} />,
      },
    ],
    [],
  );

  const users = React.useMemo(() => data, [props]);

  const tableInstance = useCustomTable({
    columns,
    data: users,
    manualSortBy: true,
    pagy_metadata,
    sortBy,
  });

  useEffect(() => {
    if (!loadRef.current) {
      loadRef.current = true;
      return;
    }

    refetch(searchFilter);
  }, [userTypeFilter, tableInstance.state.sortBy]);

  return (
    <div>
      <div className="mb-4">
        <span className="inline-flex rounded-md shadow-sm">
          <button
            onClick={navigateToNewUserPath}
            type="button"
            className="inline-flex items-center px-4 py-1 border border-transparent text-sm leading-5 font-medium rounded-md text-white bg-indigo-700 hover:bg-indigo-500 focus:outline-none focus:border-indigo-700 focus:shadow-outline-indigo active:bg-indigo-700 transition ease-in-out duration-150">
            Add
          </button>
        </span>
      </div>

      <div className="flex items-center mb-6">
        <RoleInput
          multiselect
          value={userTypeFilter}
          handleChange={(value) => setUserTypeFilter(value)}
        />
        <SearchInput
          name="search_string"
          placeholder="Search by name"
          value={searchFilter}
          handleChange={(value) => setSearchFilter(value)}
          onSearch={() => refetch(searchFilter)}
          onClear={clearSearch}
        />
      </div>

      <Table
        onEdit={handleOnEdit}
        pagy_metadata={pagy_metadata}
        onDelete={handleOnDelete}
        tableInstance={tableInstance}
      />
    </div>
  );

  function navigateToNewUserPath() {
    Inertia.visit('/users/new');
  }

  async function handleOnDelete(id: string) {
    await Inertia.delete(`/users/${id}`);
  }

  async function handleOnEdit(id: string) {
    await Inertia.visit(`/users/${id}/edit`);
  }

  function clearSearch() {
    setSearchFilter('');
    refetch('');
  }

  function refetch(search_string) {
    const { sortBy } = tableInstance.state;

    const query = {
      user_types: userTypeFilter,
    };

    if (sortBy.length) {
      const { id, desc } = sortBy[0];

      query['sort_by'] = `${id}_${desc ? 'DESC' : 'ASC'}`;
    }

    if (search_string) {
      query['search_string'] = search_string;
    }

    Inertia.get('/users', query, {
      preserveState: true,
      replace: true,
      only: ['data', 'pagy_metadata'],
    });
  }
}

Index.layout = (page: React.ReactNode) => <PageLayout title="Users">{page}</PageLayout>;
